import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { mapObject, orderBy } from './data-utils';
import { useSelector } from './hook-utils';
import { DeviceGroupType, Identifier, Presence } from './model';
import { actions } from './store';
import { getConfig, selectExtendedAvailablePresenceEnabled, selectIsWebRtcEnabled, selectMyAccess, selectMyself, selectPresenceMap } from './store/reducers/config';
import { getNow } from './store/reducers/now';
import { selectExtension, selectPresence } from './store/reducers/queueAgents';

const extendedAvailable = [{
  type: DeviceGroupType.Desk,
  name: 'Available Desk',
}, {
  type: DeviceGroupType.Associate,
  name: 'Available Associate',
}, {
  type: DeviceGroupType.Home,
  name: 'Available Home',
}, {
  type: DeviceGroupType.Mobile,
  name: 'Available Mobile',
}, {
  type: DeviceGroupType.Other,
  name: 'Available Other',
}]

export function usePresence(ext: Identifier | null) {
  const presenceMap = useSelector(selectPresenceMap)

  const presenceValues = useMemo(() => mapObject(presenceMap.presence, (id, presence) => ({
    presence: parseInt(id) as Presence,
    name: presence.description,
  })).filter(x => x.presence >= -1).sort(orderBy<any>(x => x.presence)), [presenceMap])

  const breakCodes = useMemo(() =>
    [{ id: 0, name: 'On A Break' }, ...mapObject(presenceMap.breakCodes, (id, name) => ({ id: parseInt(id), name }))],
    [presenceMap])

  const presence = useSelector(s => selectPresence(s, ext))
  const showExtended = useSelector(selectExtendedAvailablePresenceEnabled)
  const access = useSelector(selectMyAccess)
  const dispatch = useDispatch()

  const setPresence = useCallback((presence: Presence, type?: DeviceGroupType, breakCode?: number) => {
    if (!ext) { return }
    if (presence === Presence.Break && breakCode === undefined) {
      breakCode = 0
    }
    dispatch(actions.setPresence(ext, presence, type, breakCode))
  }, [dispatch, ext])

  return {
    presenceValues, presence, setPresence,
    extendedAvailable: showExtended ? extendedAvailable : null,
    breakCodes: access.features.breakCodes ? breakCodes : null,
    operatorNotes: presence.operatorNotes,
    returnTime: presence.returnTime,
  }
}

export function useAlarm(time: number | null, onAlarm: () => void) {
  const now = useSelector(getNow)
  const triggered = useRef(false)
  const timeRef = useRef(time)

  useEffect(() => {
    if (timeRef.current !== time) {
      timeRef.current = time
      triggered.current = false
    }
  }, [time])

  useEffect(() => {
    if (time !== null && now > time && !triggered.current) {
      triggered.current = true
      onAlarm()
    }
  }, [now, onAlarm, time])
}

export function useIsSoftphoneEnabled() {
  const myself = useSelector(selectMyself)
  const extension = useSelector(s => myself && selectExtension(s, myself))
  const config = useSelector(getConfig)
  const access = useSelector(selectMyAccess)
  const webRtcEnabled = useSelector(selectIsWebRtcEnabled)

  return useMemo(() => {
    if (!myself || !extension || !config || !access) {
      return false
    }

    if (access.isTeamsPbx && extension.deviceGroupType === DeviceGroupType.Other) {
      // deliver via teams
      return false
    }

    return ((access.isTeamsPbx && extension.deviceGroupType === DeviceGroupType.Desk) || (!access.isTeamsPbx)) && webRtcEnabled
  }, [myself, extension, config, access, webRtcEnabled])
}
